import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createContext, useEffect, useState } from "react";
import { DashboardService } from "../../../Services/DashboardService";
import { GET } from "../../../Utilities/BaseService";
import { DCardVariant, DashboardCard } from "./DashboardCard";

type Dashboard = {
    connections: {
        count: {
            [key: string]: any;
        };
        amount: {
            [key: string]: any;
        };
    };
    bill: {
        amount: { [key: string]: any };
    };
    totalAdjustment: number | "";
    totalUnitsGenerated: number | "";
    surcharge: {
        total: number | "";
        payed: number | "";
        remaining: number | "";
    };

    billsCount: {
        total: number | "";
        completed: number | "";
        remaining: number | "";
    };
};
type Filter = {
    district?: any;
    village?: number[];
    hamlet?: number[];
    powerstation?: number[];
    startDate?: string;
    endDate?: string;
    apply?: any;
};

const targetFilter = (filter: Filter) =>
    Object.fromEntries(
        Object.entries(filter).filter(
            ([k, v]) => v && v !== ("all" as any) && v.length > 0
        )
    );

const getPaymentReport = (filter: any) => {
    return GET(
        "/reports/dashboard/collection-reading-amounts",
        targetFilter(filter)
    );
};

export const SimpleDashboardCtx = createContext({} as any);

export const SimpleDashboard = ({ filter, setFilter, paymentFilter }: any) => {
    const [status, setStatus] = useState({
        connAmt: "idle",
        connCount: "idle",
        billAmt: "idle",
        adjsAmt: "idle",
        totalUnitsGenerated: "idle",
        surchargeCount: "idle",
        billsCount: "idle",
    });

    const [year, month] = filter.month?.split("-") ?? ["", ""];

    const [paymentReport, setPaymentReport] = useState<any>({});

    const [dashbaord, setDashbaord] = useState<Dashboard>({
        connections: {
            count: {
                Domestic: 0,
                Commercial: 0,
            },
            amount: {
                Domestic: 0,
                Commercial: 0,
            },
        },
        bill: {
            amount: {},
        },
        totalAdjustment: "",
        totalUnitsGenerated: "",
        surcharge: { total: "", payed: "", remaining: "" },
        billsCount: { total: "", completed: "", remaining: "" },
    });

    const getDashboardCount = async () => {
        setStatus((s) => ({ ...s, connCount: "loading" }));
        const connCountReport =
            await DashboardService.getInstance().getConnectionsCount({
                ...filter,
                connectionType: paymentFilter.connectionType,
                month: month,
                year: year,
            });

        if (connCountReport.data) {
            setStatus((s) => ({ ...s, connCount: "success" }));
            setDashbaord((d) => ({
                ...d,
                connections: {
                    ...d.connections,
                    count: connCountReport.data,
                },
            }));
        } else {
            setStatus((s) => ({ ...s, connCount: "error" }));
        }
    };

    const { data: _paymentReport, isFetching: isPaymentFetching } = useQuery(
        ["Payment Report", { ...filter, paymentFilter }],
        () =>
            getPaymentReport({
                ...filter,
                ...paymentFilter,
                month: month,
                year: year,
            }),
        {
            onSuccess: (res) => {
                setPaymentReport(res.data.paymentReport);
            },
        }
    );

    const getDashboardAmounts = async () => {
        setStatus((s) => ({ ...s, connAmt: "loading" }));
        const connAmtReport =
            await DashboardService.getInstance().getConnectionsAmount({
                ...filter,
                month: month,
                year: year,
            });

        if (connAmtReport.data) {
            setStatus((s) => ({ ...s, connAmt: "success" }));
            setDashbaord((d) => ({
                ...d,
                connections: {
                    ...d.connections,
                    amount: connAmtReport.data,
                },
            }));
        } else {
            setStatus((s) => ({ ...s, connAmt: "error" }));
        }
    };

    const getBillAmounts = async () => {
        setStatus((s) => ({ ...s, billAmt: "loading" }));
        const billAmounts =
            await DashboardService.getInstance().getReadingAmount({
                ...filter,
                month: month,
                year: year,
            });

        if (billAmounts.data) {
            setStatus((s) => ({ ...s, billAmt: "success" }));
            setDashbaord((d) => ({
                ...d,
                bill: {
                    ...d.bill,
                    amount: billAmounts.data,
                },
            }));
        } else {
            setStatus((s) => ({ ...s, billAmt: "error" }));
        }
    };

    const getAdjustedAmount = async () => {
        setStatus((s) => ({ ...s, adjsAmt: "loading" }));
        const adjAmtRes =
            await DashboardService.getInstance().getAdjustedAmount({
                ...filter,
                connectionType: paymentFilter.connectionType,
                month: month,
                year: year,
            });

        if (adjAmtRes.data) {
            setStatus((s) => ({ ...s, adjsAmt: "success" }));
            setDashbaord((d) => ({
                ...d,
                totalAdjustment: adjAmtRes.data.adj.totalAdjustment,
            }));
        } else {
            setStatus((s) => ({ ...s, adjsAmt: "error" }));
        }
    };

    const getTotalUnitsGenerated = async () => {
        setStatus((s) => ({ ...s, totalUnitsGenerated: "loading" }));
        const totalUnitsRes =
            await DashboardService.getInstance().getTotalGeneratedUnits({
                ...filter,
                connectionType: paymentFilter.connectionType,
                month: month,
                year: year,
            });

        if (totalUnitsRes.data) {
            setStatus((s) => ({ ...s, totalUnitsGenerated: "success" }));
            setDashbaord((d) => ({
                ...d,
                totalUnitsGenerated: totalUnitsRes.data.totalUnits,
            }));
        } else {
            setStatus((s) => ({ ...s, totalUnitsGenerated: "error" }));
        }
    };

    const getBillsCount = async () => {
        setStatus((s) => ({ ...s, billsCount: "loading" }));
        const billsCountRes =
            await DashboardService.getInstance().getBillsCount({
                ...filter,
                connectionType: paymentFilter.connectionType,
                month: month,
                year: year,
            });

        if (billsCountRes.data) {
            setStatus((s) => ({ ...s, billsCount: "success" }));
            setDashbaord((d) => ({
                ...d,
                billsCount: {
                    total: billsCountRes.data.total,
                    completed: billsCountRes.data.completedBills,
                    remaining: billsCountRes.data.remainingBills,
                },
            }));
        } else {
            setStatus((s) => ({ ...s, billsCount: "error" }));
        }
    };

    useEffect(() => {
        if (filter.apply) {
            getDashboardCount();
            getDashboardAmounts();
            getBillAmounts();
            getAdjustedAmount();
            getTotalUnitsGenerated();
            getBillsCount();
        }
    }, [filter.apply]);

    return (
        <SimpleDashboardCtx.Provider value={{ status }}>
            <Grid container spacing={1}>
                {Object.entries(dashbaord.connections["count"] || {}).map(
                    ([key, value]) => (
                        <Grid item xs={12} md={4} key={key}>
                            <DashboardCard
                                variant={DCardVariant.Simple}
                                data={{
                                    label: `${key} connections`,
                                    value: value || 0,
                                    ident: "connCount",
                                }}
                            />
                        </Grid>
                    )
                )}

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total connections",
                            value: Object.values(
                                dashbaord.connections.count
                            ).reduce((curr, acc) => curr + acc, 0),
                            ident: "connCount",
                        }}
                    />
                </Grid>

                {Object.entries(dashbaord.connections["amount"] || {}).map(
                    ([key, value]) => (
                        <Grid item xs={12} md={4} key={key}>
                            <DashboardCard
                                variant={DCardVariant.Simple}
                                data={{
                                    label: `${key} connection charges amount`,
                                    value: value || 0,
                                    ident: "connAmt",
                                }}
                            />
                        </Grid>
                    )
                )}

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total connection charges amount",
                            value: Object.values(
                                dashbaord.connections.amount || {}
                            ).reduce((acc, curr) => acc + curr, 0),
                            ident: "connAmt",
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total amount generated",
                            value:
                                paymentReport.totalAmount +
                                paymentReport.applicableSurcharge +
                                paymentReport.additionalCharges +
                                paymentReport.connectionCharges,
                            ident: "billAmt",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total amount collected",
                            value: Math.min(
                                paymentReport.collection_amount +
                                    paymentReport.collection_adjustment +
                                    paymentReport.paidSurcharge +
                                    paymentReport.pcCollected,
                                paymentReport.totalAmount +
                                    paymentReport.applicableSurcharge +
                                    paymentReport.additionalCharges +
                                    paymentReport.connectionCharges
                            ),
                            ident: "billAmt",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Arrears remaining",
                            value: Math.max(
                                0,
                                Math.min(
                                    paymentReport.totalAmount +
                                        paymentReport.applicableSurcharge +
                                        paymentReport.additionalCharges +
                                        paymentReport.connectionCharges -
                                        (paymentReport.collection_amount +
                                            paymentReport.collection_adjustment +
                                            paymentReport.paidSurcharge +
                                            paymentReport.pcCollected),
                                    paymentReport.totalAmount +
                                        paymentReport.applicableSurcharge +
                                        paymentReport.additionalCharges +
                                        paymentReport.connectionCharges
                                )
                            ),
                            ident: "billAmt",
                        }}
                    />
                </Grid>

                <Grid item xs={6}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total Adjustment",
                            value: dashbaord.totalAdjustment,
                            ident: "adjsAmt",
                        }}
                    />
                </Grid>

                <Grid item xs={6}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total Units Generated",
                            value: dashbaord.totalUnitsGenerated,
                            ident: "totalUnitsGenerated",
                        }}
                    />
                </Grid>

                {/* <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total Surcharge",
                            value: dashbaord.surcharge.total,
                            ident: "surchargeCount",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Payed Surcharge",
                            value: dashbaord.surcharge.payed,
                            ident: "surchargeCount",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Remaining Surcharge",
                            value: dashbaord.surcharge.remaining,
                            ident: "surchargeCount",
                        }}
                    />
                </Grid> */}

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Total bills generated",
                            value: dashbaord.billsCount.total,
                            ident: "billsCount",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Completed bills",
                            value: dashbaord.billsCount.completed || 0,
                            ident: "billsCount",
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <DashboardCard
                        variant={DCardVariant.Simple}
                        data={{
                            label: "Remaining bills",
                            value: dashbaord.billsCount.remaining || 0,
                            ident: "billsCount",
                        }}
                    />
                </Grid>
            </Grid>
        </SimpleDashboardCtx.Provider>
    );
};
